import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Alert, Spinner } from '@/components/ui';
import type { Organization, User } from '@/services/admin/AdminService';

interface OrganizationMembersModalProps {
  open: boolean;
  onClose: () => void;
  organization: Organization | null;
  onFetchMembers: (orgId: string) => Promise<User[]>;
  onRemoveMember: (orgId: string, uid: string) => Promise<boolean>;
}

export const OrganizationMembersModal: React.FC<
  OrganizationMembersModalProps
> = ({ open, onClose, organization, onFetchMembers, onRemoveMember }) => {
  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removingUid, setRemovingUid] = useState<string | null>(null);

  useEffect(() => {
    if (open && organization) {
      setLoading(true);
      setError(null);
      onFetchMembers(organization.id)
        .then((users) => setMembers(users))
        .catch((err) =>
          setError(
            err instanceof Error ? err.message : 'Failed to fetch members'
          )
        )
        .finally(() => setLoading(false));
    }
  }, [open, organization, onFetchMembers]);

  const handleRemoveMember = async (uid: string) => {
    if (!organization) return;

    setRemovingUid(uid);
    const success = await onRemoveMember(organization.id, uid);
    if (success) {
      setMembers((prev) => prev.filter((m) => m.uid !== uid));
    }
    setRemovingUid(null);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Members - ${organization?.name || ''}`}
      maxWidth="md"
      actions={
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : members.length === 0 ? (
        <Alert variant="info">No members in this organization.</Alert>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-secondary-600 mb-4">
            {members.length} member{members.length !== 1 ? 's' : ''} in this
            organization
          </p>
          <div className="divide-y divide-secondary-100">
            {members.map((member) => (
              <div
                key={member.uid}
                className="flex items-center justify-between py-3"
              >
                <div className="flex-1">
                  <p className="font-medium text-secondary-900">
                    {member.email}
                  </p>
                  <p className="text-sm text-secondary-500">
                    {member.display_name || 'No display name'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {member.systemRole && (
                    <Badge
                      variant={
                        member.systemRole === 'ADMIN' ? 'primary' : 'default'
                      }
                    >
                      {member.systemRole}
                    </Badge>
                  )}
                  <Badge
                    variant={member.is_active !== false ? 'success' : 'default'}
                  >
                    {member.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveMember(member.uid)}
                    loading={removingUid === member.uid}
                    className="text-error-600 hover:text-error-700"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default OrganizationMembersModal;
